import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../common/debounce";
import { Card } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useTranslation } from "react-i18next";
import CommonPagination from "../../components/widgets/common_pagination";
import { CommonTextField } from "../../components/widgets/common_textField";
import CommonButton from "../../components/widgets/common_button";
import Delete from "./Delete";
import EditUser from "./EditUser";
import UserService from "../../service/user.service";
import Toaster from "../../components/common/toaster";
import { Eye, X, Users } from "lucide-react";

const User = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  // Initialize state with potential restoration from localStorage
  const getInitialState = () => {
    const savedPaginationState = localStorage.getItem("usersPaginationState");
    if (savedPaginationState) {
      try {
        const paginationState = JSON.parse(savedPaginationState);
        const isRecent =
          Date.now() - paginationState.timestamp < 30 * 60 * 1000;
        if (isRecent) {
          localStorage.removeItem("usersPaginationState");
          return {
            page: paginationState.page || 1,
            size: paginationState.size || 10,
            search: paginationState.search || "",
            searchInput: paginationState.search || "",
            userTypeFilter: paginationState.userTypeFilter || "employers",
          };
        } else {
          localStorage.removeItem("usersPaginationState");
        }
      } catch (error) {
        localStorage.removeItem("usersPaginationState");
        // Clear corrupted state
        localStorage.removeItem("usersPaginationState");
      }
    }
    return {
      page: 1,
      size: 10,
      search: "",
      searchInput: "",
      userTypeFilter: "employers",
    };
  };

  const initialState = getInitialState();
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(initialState.page);
  const [size, setSize] = useState(initialState.size);
  const [search, setSearch] = useState(initialState.search);
  const [searchInput, setSearchInput] = useState(initialState.searchInput);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [userTypeFilter, setUserTypeFilter] = useState(
    initialState.userTypeFilter
  );
  const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);
  const [selectedUserCategories] = useState([]);
  const [userDataDialogOpen, setUserDataDialogOpen] = useState(false);
  const [selectedUserData] = useState(null);
  const [statusChangingUsers, setStatusChangingUsers] = useState(new Set());
  const [dashboardMetrics, setDashboardMetrics] = useState({
    totalEmployees: 0,
    totalJobSeekers: 0,
  });
  const isMountedRef = useRef(true);
  const isInitialLoadRef = useRef(true);

  const imagePlaceholder =
    "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";

  // Fetch dashboard metrics
  const fetchDashboardMetrics = useCallback(async () => {
    try {
      // Use Promise.all to fetch both metrics simultaneously
      const [employeesResponse, jobSeekersResponse] = await Promise.all([
        UserService.getUsers({
          page: 1,
          limit: 1, // Only fetch 1 record to get the total count
          userType: "employers",
        }),
        UserService.getUsers({
          page: 1,
          limit: 1, // Only fetch 1 record to get the total count
          userType: "job_seekers",
        }),
      ]);

      const totalEmployees = employeesResponse?.data?.total || 0;
      const totalJobSeekers = jobSeekersResponse?.data?.total || 0;

      setDashboardMetrics({
        totalEmployees,
        totalJobSeekers,
      });
    } catch (error) {
      console.error("Failed to fetch dashboard metrics:", error);
    }
  }, []);

  // Fetch users from API
  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: size,
      };

      // Only add search parameter if it's not empty
      if (search && search.trim() !== "") {
        params.search = search.trim();
      }

      // Add userType filter
      params.userType = userTypeFilter;

      const response = await UserService.getUsers(params);

      if (response?.data?.data) {
        setUsers(response.data.data);
        setTotalUsers(response.data.total || response.data.data.length);
        setTotalPages(
          Math.ceil((response.data.total || response.data.data.length) / size)
        );
      }
    } catch (error) {
      Toaster(
        "error",
        error?.response?.data?.message || "Failed to fetch users"
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, size, search, userTypeFilter]);

  // Load users on component mount and when page/size changes
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, search, userTypeFilter]);

  // Load dashboard metrics on component mount
  useEffect(() => {
    fetchDashboardMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search function
  const debouncedSearch = useDebounce((searchTerm) => {
    setSearch(searchTerm);
    // Only reset page if we're not restoring state
    if (!isInitialLoadRef.current) {
      setPage(1); // Reset to first page when searching
    }
  }, 500);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value); // Update input immediately for visual feedback

    // Skip debounced search during initial load to prevent page reset
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    debouncedSearch(value); // Debounced API call
  };

  const handleClearSearch = () => {
    setSearchInput(""); // Clear input immediately
    setSearch(""); // Clear search state
    setPage(1); // Reset to first page
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDataSize = (value) => {
    setSize(value);
    setPage(1);
  };

  const handleUserTypeFilterChange = (value) => {
    setUserTypeFilter(value);
    localStorage.setItem("userTypeFilter", value); // Save to localStorage
    setPage(1); // Reset to first page when filter changes
  };

  const handleViewUserData = (user) => {
    // Save current pagination state to localStorage
    const paginationState = {
      page,
      size,
      search,
      userTypeFilter,
      timestamp: Date.now(),
    };
    localStorage.setItem(
      "usersPaginationState",
      JSON.stringify(paginationState)
    );

    navigate(`/users/${user._id}`, {
      state: {
        user,
        from: "/users",
        currentFilter: userTypeFilter, // Pass the current filter state
        currentPage: page, // Pass the current page state
        currentSize: size, // Pass the current page size
        currentSearch: search, // Pass the current search term
      },
    });
  };

  const handleStatusChange = async (index, userStatus, user) => {
    setStatusChangingUsers((prev) => new Set(prev).add(user._id));

    try {
      await UserService.toggleUserActive(user._id);

      // ✅ Update local state immediately
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u._id === user._id ? { ...u, isActive: userStatus === "un-block" } : u
        )
      );

      // ✅ Custom message based on action
      Toaster(
        "success",
        `User ${
          userStatus === "un-block" ? "unblocked" : "blocked"
        } successfully`
      );
    } catch (error) {
      Toaster(
        "error",
        error?.response?.data?.message || "Failed to update user status"
      );
    } finally {
      setStatusChangingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user._id);
        return newSet;
      });
    }
  };

  // Format text: capitalize first character and remove underscores
  const formatText = (text) => {
    if (!text) return "-";
    return text
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
  };

  return (
    <div className="space-y-1">
      {/* Sticky Dashboard Metrics */}
      <div className="sticky top-0 z-10 pb-4 flex-shrink-0">
        <div className="flex justify-start gap-6">
          {/* Total Job Seekers */}
          <Card className="p-6 rounded-lg shadow-sm h-24 w-64">
            <div className="flex items-center gap-4 h-full">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Job Seekers</p>
                <p className="text-3xl font-bold text-blue-600">
                  {dashboardMetrics.totalJobSeekers}
                </p>
              </div>
            </div>
          </Card>

          {/* Total Employees */}
          <Card className="p-6 rounded-lg shadow-sm h-24 w-64">
            <div className="flex items-center gap-4 h-full">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Employees</p>
                <p className="text-3xl font-bold text-purple-600">
                  {dashboardMetrics.totalEmployees}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Divider */}
        <div className="mt-4 border-b border-gray-200"></div>
      </div>

      {/* User List Section */}
      <Card className="p-10 grid gap-6">
        <div className="flex items-center justify-between gap-4 max-lg:flex-col">
          {/* Left side - User List heading */}
          <h3 className="text-xl tab:text-2xl font-bold">
            {t("users.userList")}
          </h3>

          {/* Right side - Search bar and filter */}
          <div className="flex gap-4 w-full max-w-xl justify-end">
            <div className="lg:max-w-72 w-full grid gap-1">
              <div className="relative">
                <CommonTextField
                  type="text"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder={t("searchPlaceholder")}
                  className="w-full pr-10"
                />
                {searchInput && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="w-48">
              <Select
                value={userTypeFilter}
                onValueChange={handleUserTypeFilterChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by user type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employers">Employers</SelectItem>
                  <SelectItem value="job_seekers">Job Seekers</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[410px]">
          <Table className="whitespace-nowrap">
            <TableHeader>
              <TableRow>
                <TableHead className="w-14">No</TableHead>
                <TableHead className="w-20">Profile Image</TableHead>
                <TableHead>First Name</TableHead>
                <TableHead>Last Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>User Status</TableHead>
                <TableHead>Action</TableHead>
                {/* <TableHead>Job Categories</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="h-40">
                  <TableCell
                    colSpan={8}
                    className="text-center text-black/50 font-medium text-xl"
                  >
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : users.length > 0 ? (
                users.map((item, index) => (
                  <TableRow key={item._id || index}>
                    <TableCell className="ps-4">
                      {(page - 1) * size + index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center">
                        <div className="h-8 w-8 overflow-hidden rounded-lg">
                          <img
                            src={item.profilePic || imagePlaceholder}
                            alt="User"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{item.firstName || "-"}</TableCell>
                    <TableCell>{item.lastName || "-"}</TableCell>
                    <TableCell>{item.email || "-"}</TableCell>
                    <TableCell>{formatText(item.role)}</TableCell>
                    <TableCell className="w-32 text-center">
                      <div className="flex justify-center">
                        {statusChangingUsers.has(item._id) ? (
                          <div className="w-32 h-10 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                          </div>
                        ) : (
                          <Select
                            onValueChange={(value) =>
                              handleStatusChange(index, value, item)
                            }
                            defaultValue={item.isActive ? "un-block" : "block"}
                          >
                            <SelectTrigger
                              className={`w-32 font-medium ${
                                item.isActive
                                  ? "!border-green-500 text-green-600 bg-green-50"
                                  : "!border-red-500 text-red-600 bg-red-50"
                              }`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="block">
                                {t("users.block")}
                              </SelectItem>
                              <SelectItem value="un-block">
                                {t("users.unBlock")}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <CommonButton
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewUserData(item)}
                        className="text-xs p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Eye className="h-4 w-4" />
                      </CommonButton>
                    </TableCell>
                    {/* <TableCell>
                  {item.jobCategories && item.jobCategories.length > 0 ? (
                    <CommonButton
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewCategories(item.jobCategories)}
                      className="text-xs"
                    >
                      View All
                    </CommonButton>
                  ) : (
                    <span className="text-gray-500 text-sm">No categories</span>
                  )}
                </TableCell> */}
                  </TableRow>
                ))
              ) : (
                <TableRow className="h-40">
                  <TableCell
                    colSpan={8}
                    className="text-center text-black/50 font-medium text-xl"
                  >
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between max-md:flex-col gap-4">
          {totalUsers > size && (
            <CommonPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(newPage) => setPage(newPage)}
              pageSize={size}
              onPageSizeChange={handleDataSize}
              className=""
            />
          )}
        </div>

        <Delete
          isOpen={isOpen === "delete"}
          setIsOpen={setIsOpen}
          isDelete={isOpen}
        />
        <EditUser
          isOpen={isOpen === "edit"}
          setIsOpen={setIsOpen}
          isEdit={isOpen}
        />

        {/* Categories Dialog */}
        <Dialog
          open={categoriesDialogOpen}
          onOpenChange={setCategoriesDialogOpen}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Job Categories</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {selectedUserCategories.length > 0 ? (
                <div className="space-y-2">
                  {selectedUserCategories.map((category, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-blue-600 font-medium">•</span>
                      <span className="text-sm">{category}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No categories available</p>
              )}
            </div>
            <DialogFooter>
              <CommonButton
                variant="outline"
                onClick={() => setCategoriesDialogOpen(false)}
              >
                Close
              </CommonButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Data Dialog */}
        <Dialog open={userDataDialogOpen} onOpenChange={setUserDataDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {selectedUserData ? (
                <div className="grid gap-4">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 overflow-hidden rounded-lg">
                      <img
                        src={selectedUserData.profilePic || imagePlaceholder}
                        alt="User Profile"
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        {selectedUserData.firstName} {selectedUserData.lastName}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {selectedUserData.email}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        First Name
                      </label>
                      <p className="text-sm">
                        {selectedUserData.firstName || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Last Name
                      </label>
                      <p className="text-sm">
                        {selectedUserData.lastName || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <p className="text-sm">{selectedUserData.email || "-"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Role
                      </label>
                      <p className="text-sm">
                        {formatText(selectedUserData.role)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Status
                      </label>
                      <p className="text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            selectedUserData.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {selectedUserData.isActive ? "Active" : "Blocked"}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        User ID
                      </label>
                      <p className="text-xs font-mono">
                        {selectedUserData._id || "-"}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No user data available</p>
              )}
            </div>
            <DialogFooter>
              <CommonButton
                variant="outline"
                onClick={() => setUserDataDialogOpen(false)}
              >
                Close
              </CommonButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
};

export default User;
